import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectActivatedFireId } from "features/map/control/selectors"
import { selectVisibleSatelliteFlights } from "features/entity-caching/flights/selectors"
import { selectLastSelectedLayerId } from "panels/content/side-panel/slice.new"
import { useAppSelector } from "integrations/redux/hooks"
import { CogOrthoLayer } from "plugins/Orthomosaic/layers/CogOrthoLayer"
import { toImageData } from "plugins/Orthomosaic/layers/rasterImageData"
import { selectSatelliteOverlapIsVisible } from "./SatelliteOverlapSlice"
import { cogOrthoBbox, selectActiveCogOrtho } from "./layers/satelliteLayer"
import { overlapColorForIndex } from "./overlapColor"
import { indexCogIdForCloudFree } from "./provenance/provenanceTileSampling"

const OVERLAP_FILL_ALPHA = 178 // ~70% opacity for colored pixels

// scene_index.cog.tif is single-band (index replicated into R) plus an alpha coverage mask.
function transformSceneIndexTile(tileImage: ImageBitmap | ImageData): ImageData {
    const { data, width, height } = toImageData(tileImage)
    const out = new ImageData(width, height)
    for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] === 0) continue
        const [r, g, b] = overlapColorForIndex(data[i])
        out.data[i] = r
        out.data[i + 1] = g
        out.data[i + 2] = b
        out.data[i + 3] = OVERLAP_FILL_ALPHA
    }
    return out
}

export const useSatelliteOverlapLayer = () => {
    const workspaceId = useAppSelector(selectActiveWorkspaceId())
    const fireId = useAppSelector(selectActivatedFireId)
    const activeLayerId = useAppSelector(selectLastSelectedLayerId)
    // Only from the visible-flights list, so a hidden flight's overlap never renders even if still selected.
    const visibleFlights = useAppSelector(selectVisibleSatelliteFlights)
    const flight = visibleFlights.find((f) => f.id === activeLayerId)
    const isOverlapVisible = !!useAppSelector(selectSatelliteOverlapIsVisible(activeLayerId ?? ""))

    const cogOrtho = flight && selectActiveCogOrtho(flight)
    if (!isOverlapVisible || !flight || !cogOrtho || !workspaceId || !fireId) return []

    const indexCogId = indexCogIdForCloudFree(!!flight.cloudFree)

    return [
        CogOrthoLayer({
            // cogId in the id forces a fresh layer on toggle instead of reusing a stale tile cache.
            id: `satellite-overlap-${flight.id}-${indexCogId}`,
            orgId: workspaceId,
            projectId: fireId,
            flightId: flight.demoId || flight.id,
            source: "satellite",
            cogId: indexCogId,
            isDemo: !!flight.demoId,
            bbox: cogOrthoBbox(cogOrtho),
            maxZoom: cogOrtho.maxZoom || 21,
            minZoom: cogOrtho.minZoom || 8,
            opacity: 1,
            transform: transformSceneIndexTile,
        }),
    ]
}
